import { useState, useMemo, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import { useSuppliers } from '../../hooks/useSuppliers'
import { useSupplierMitigationTasks } from '../../hooks/useSupplierMitigationTasks'
import { useSupplierVisibility } from '../../hooks/useSupplierVisibility'
import {
  useAllSpecialReports,
  useSupplierSpecialReportsMutations,
  getDownloadUrl,
} from '../../hooks/useSupplierSpecialReports'
import { useAdmin } from '../../hooks/useAdmin'
import { invokeSuggestSupplierRisk } from '../../services/invokeSuggestSupplierRisk'
import { supabase } from '../../services/supabase'
import { toErrorMessage } from '../../utils/toErrorMessage'
import {
  parseExcelFile,
  getSupplierRowsFromSheet,
  mergeSupplierRows,
  supplierRowMergeKey,
  isTasksOrNewsSheetName,
  supplierRowsToInsertPayload,
  upsertSuppliersInChunks,
  upsertUserGroupRows,
  taskRowsToInsertPayload,
  insertMitigationTasksInChunks,
  newsRowsToInsertPayload,
  filterExistingNewsPayloads,
  insertNewsRowsInChunks,
  downloadSuppliersTemplate,
  type SupplierRow,
  type TaskRow,
  type ParseResult,
} from '../../utils/excelImport'
import { buildSupplierNameIndex } from '../../utils/companyNameMatch'
import { runBackfillEmbeddingsUntilDone } from '../../services/invokeBackfillEmbeddings'
import { invalidateSupplierRosterCache } from '../../services/supplierRosterCache'
import Modal from '../ui/Modal'
import SpendCubeImportPanel from './SpendCubeImportPanel'
import type { Supplier, SupplierMitigationTask, SupplierMitigationStatus, SupplierMitigationPriority, SupplierSpecialReport } from '../../types'
import { supplierDisplayName } from '../../apps/early-signals/supplierRollup'

const STATUS_OPTIONS: SupplierMitigationStatus[] = [
  'open',
  'on_hold',
  'closed',
  'scheduled',
  'in_progress',
  'at_risk',
  'completed',
]
const PRIORITY_OPTIONS: SupplierMitigationPriority[] = ['low', 'medium', 'high', 'critical']

function TaskEditRow({
  task,
  onSave,
  onCancel,
}: {
  task: SupplierMitigationTask
  onSave: (updates: Partial<SupplierMitigationTask>) => Promise<void>
  onCancel: () => void
}) {
  const [taskText, setTaskText] = useState(task.task)
  const [status, setStatus] = useState<SupplierMitigationStatus>(task.status)
  const [priority, setPriority] = useState<SupplierMitigationPriority>(task.priority)
  const [saving, setSaving] = useState(false)
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="text"
        value={taskText}
        onChange={(e) => setTaskText(e.target.value)}
        className="flex-1 min-w-[120px] border border-gray-300 rounded px-2 py-1 text-sm"
      />
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value as SupplierMitigationStatus)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <select
        value={priority}
        onChange={(e) => setPriority(e.target.value as SupplierMitigationPriority)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
      >
        {PRIORITY_OPTIONS.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={async () => {
          setSaving(true)
          await onSave({ task: taskText, status, priority })
          setSaving(false)
        }}
        disabled={saving || !taskText.trim()}
        className="text-blue-600 hover:underline text-sm disabled:opacity-50"
      >
        Save
      </button>
      <button type="button" onClick={onCancel} className="text-gray-600 hover:underline text-sm">
        Cancel
      </button>
    </div>
  )
}

function riskScoresToJson(risk_scores: Record<string, unknown>): string {
  try {
    return JSON.stringify(risk_scores, null, 2)
  } catch {
    return '{}'
  }
}

/** Derive risk category from overall_score when risk_rating is empty (Low ≤4, Medium ≤6, High >6). */
function riskCategory(s: Supplier): string {
  if (s.risk_rating != null && String(s.risk_rating).trim() !== '') return s.risk_rating
  const score = s.overall_score
  if (score == null || typeof score !== 'number' || !Number.isFinite(score)) return '—'
  if (score > 6) return 'High'
  if (score > 4) return 'Medium'
  if (score >= 1) return 'Low'
  return '—'
}

/** Junk / header-like names that should be deleted from cleanup. */
function isJunkSupplierName(s: Pick<Supplier, 'name'>): boolean {
  const name = (s.name ?? '').trim()
  return (
    !name ||
    name === '.' ||
    name.toLowerCase() === 'supplier' ||
    name.toLowerCase() === '(blank)' ||
    name.includes('Confidential') ||
    name.includes('All rights reserved')
  )
}

function supplierHasRiskScores(s: Pick<Supplier, 'overall_score' | 'risk_scores'>): boolean {
  const hasScore =
    s.overall_score != null && typeof s.overall_score === 'number' && Number.isFinite(s.overall_score)
  if (hasScore) return true
  const rs = s.risk_scores
  if (rs && typeof rs === 'object') {
    for (const v of Object.values(rs)) {
      if (v != null && typeof v === 'number' && Number.isFinite(v) && v !== 0) return true
    }
  }
  return false
}

/** True master identity (not junk) even when risk scores are blank — common on Indirect. */
function isUnscoredMasterRow(s: Supplier): boolean {
  if (isJunkSupplierName(s)) return false
  if (supplierHasRiskScores(s)) return false
  const hasIdentity =
    Boolean((s.category || s.industry || '').trim()) ||
    Boolean((s.parent_name || '').trim()) ||
    (s.spend_k != null && Number.isFinite(Number(s.spend_k))) ||
    Boolean((s.brand || '').trim()) ||
    s.procurement_type === 'direct' ||
    s.procurement_type === 'indirect'
  return hasIdentity
}

/** Destructive cleanup targets: junk names or empty husks with no master identity. */
function isDeletableEmptySupplier(s: Supplier): boolean {
  if (isJunkSupplierName(s)) return true
  if (supplierHasRiskScores(s)) return false
  // Keep unscored Indirect/Direct master rows (category/parent/spend/brand present)
  if (isUnscoredMasterRow(s)) return false
  return true
}

function parseRiskScoresJson(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value || '{}')
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

export type EarlySignalsAdminSection = 'suppliers' | 'import' | 'reports' | 'spend'

const SECTION_META: Record<
  EarlySignalsAdminSection,
  { title: string; intro: string; icon: string }
> = {
  suppliers: {
    title: 'Suppliers',
    intro:
      'One row per supplier–brand (Direct) or supplier–category–sub-category (Indirect). Same name can appear multiple times. Edit a row to set company visibility; empty list = global.',
    icon: 'fa-truck-field',
  },
  import: {
    title: 'Data import',
    intro:
      'Upload a Direct/Indirect master workbook (or legacy Master). Unscored Indirect rows are expected and kept.',
    icon: 'fa-file-excel',
  },
  reports: {
    title: 'Special reports',
    intro: 'Upload PDF, Word, or PowerPoint files users can download in Early Signals → Special reports.',
    icon: 'fa-file-pdf',
  },
  spend: {
    title: 'Spend cube',
    intro:
      'Upload one company’s invoice lines. Risk stays on the shared supplier. Uploading again replaces that company’s lines and keeps the name map.',
    icon: 'fa-coins',
  },
}

type EarlySignalsPanelProps = {
  section?: EarlySignalsAdminSection
  onNavigateSection?: (section: EarlySignalsAdminSection) => void
  onOpenCompanySettings?: () => void
}

export default function EarlySignalsPanel({
  section = 'suppliers',
  onNavigateSection,
  onOpenCompanySettings,
}: EarlySignalsPanelProps) {
  const queryClient = useQueryClient()
  const { companyId, isAdmin } = useAuth()
  const { suppliers, loading, error, refetch, insertSupplier, updateSupplier, deleteSupplier } = useSuppliers({ scope: 'all' })
  const { companies } = useAdmin()
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([])
  const { companyIds: visibilityCompanyIds, loading: visibilityLoading, updateVisibility, updating: visibilityUpdating } = useSupplierVisibility(selectedSupplier?.id)
  const [form, setForm] = useState<Partial<Supplier>>({
    name: '',
    location: '',
    risk_rating: '',
    industry: '',
    revenue: '',
    growth: '',
    employees: '',
    years_in_business: null,
    risk_scores: {},
    overall_score: null,
    outlook: '',
    actions: '',
  })
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [suggestedTasks, setSuggestedTasks] = useState<Array<{ task: string; status: string; priority: string }>>([])
  const [newTask, setNewTask] = useState<{ task: string; status: SupplierMitigationStatus; priority: SupplierMitigationPriority }>({
    task: '',
    status: 'scheduled',
    priority: 'medium',
  })
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [parseResult, setParseResult] = useState<ParseResult | null>(null)
  const [selectedSheetIndices, setSelectedSheetIndices] = useState<number[]>([])
  const [importing, setImporting] = useState(false)
  const [importMessage, setImportMessage] = useState<string | null>(null)
  const [cleaningUp, setCleaningUp] = useState(false)
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null)
  const [specialReportsSupplierId, setSpecialReportsSupplierId] = useState<string>('')
  const [specialReportsErrorDismissed, setSpecialReportsErrorDismissed] = useState(false)
  const specialReportsFileInputRef = useRef<HTMLInputElement>(null)

  const { reports: specialReports, loading: specialReportsLoading } = useAllSpecialReports()
  const {
    uploadReport,
    deleteReport,
    uploading: specialReportsUploading,
    uploadError: specialReportsUploadError,
    deleteError: specialReportsDeleteError,
  } = useSupplierSpecialReportsMutations()

  const emptySuppliers = useMemo(() => suppliers.filter(isDeletableEmptySupplier), [suppliers])
  const unscoredMasterSuppliers = useMemo(
    () => suppliers.filter(isUnscoredMasterRow),
    [suppliers],
  )

  const supplierSheetIndices = useMemo(() => {
    if (!parseResult?.sheets?.length) return []
    const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ')
    return parseResult.sheets
      .map((_, i) => i)
      .filter((i) => !isTasksOrNewsSheetName(norm(parseResult.sheets[i].name)))
  }, [parseResult?.sheets])

  const { rows: importPreview, errors: sheetErrors } = useMemo(() => {
    if (!parseResult?.sheets?.length || selectedSheetIndices.length === 0) return { rows: [] as SupplierRow[], errors: [] as string[] }
    const byKey = new Map<string, SupplierRow>()
    const allErrors: string[] = []
    for (const i of selectedSheetIndices) {
      const sheet = parseResult.sheets[i]
      if (!sheet) continue
      const { rows, errors } = getSupplierRowsFromSheet(sheet)
      allErrors.push(...errors)
      for (const r of rows) {
        const key = supplierRowMergeKey(r)
        if (!key || key === 'n:') continue
        const existing = byKey.get(key)
        byKey.set(key, existing ? mergeSupplierRows(existing, r) : r)
      }
    }
    return { rows: [...byKey.values()], errors: allErrors }
  }, [parseResult, selectedSheetIndices])

  const unscoredPreviewCount = useMemo(
    () =>
      importPreview.filter(
        (r) =>
          (r.overall_score == null || !Number.isFinite(Number(r.overall_score))) &&
          !(
            r.risk_scores &&
            Object.values(r.risk_scores).some(
              (v) => v != null && typeof v === 'number' && Number.isFinite(v) && v !== 0,
            )
          ),
      ).length,
    [importPreview],
  )
  const importErrors = useMemo(
    () => [...(parseResult?.errors ?? []), ...sheetErrors],
    [parseResult?.errors, sheetErrors]
  )
  const hasNoInternalData = useMemo(
    () =>
      importPreview.length > 0 &&
      !importPreview.some(
        (r) =>
          (r.location != null && r.location !== '') ||
          (r.category != null && r.category !== '') ||
          (r.industry != null && r.industry !== '') ||
          (r.kenvue_region != null && r.kenvue_region !== '') ||
          r.overall_score != null ||
          r.lead_time_days != null ||
          r.sla_pct != null
      ),
    [importPreview]
  )
  const importTaskRows: TaskRow[] = parseResult?.taskRows ?? []

  const supplierId = selectedSupplier?.id ?? null
  const { tasks, insertTask, updateTask, deleteTask } = useSupplierMitigationTasks(supplierId ?? undefined)

  const openAdd = () => {
    setSelectedSupplier(null)
    setForm({
      name: '',
      location: '',
      risk_rating: '',
      industry: '',
      revenue: '',
      growth: '',
      employees: '',
      years_in_business: null,
      risk_scores: {},
      overall_score: null,
      outlook: '',
      actions: '',
    })
    setSuggestedTasks([])
    setAiError(null)
    setSelectedCompanyIds([])
    setModalMode('add')
  }

  const openEdit = (s: Supplier) => {
    setSelectedSupplier(s)
    setForm({
      name: s.name,
      location: s.location ?? '',
      risk_rating: s.risk_rating ?? '',
      industry: s.industry ?? '',
      revenue: s.revenue ?? '',
      growth: s.growth ?? '',
      employees: s.employees ?? '',
      years_in_business: s.years_in_business ?? null,
      risk_scores: s.risk_scores ?? {},
      overall_score: s.overall_score ?? null,
      outlook: s.outlook ?? '',
      actions: s.actions ?? '',
    })
    setSuggestedTasks([])
    setAiError(null)
    setModalMode('edit')
    // Company visibility will be loaded by useSupplierVisibility hook
  }

  // Sync selectedCompanyIds with visibilityCompanyIds when they load
  useEffect(() => {
    if (modalMode === 'edit' && !visibilityLoading && visibilityCompanyIds) {
      setSelectedCompanyIds(visibilityCompanyIds)
    }
  }, [modalMode, visibilityCompanyIds, visibilityLoading])

  const closeModal = () => {
    setModalMode(null)
    setSelectedSupplier(null)
    setEditingTaskId(null)
    setSelectedCompanyIds([])
  }

  const handleGenerateWithAi = async () => {
    setAiLoading(true)
    setAiError(null)
    try {
      const { suggested } = await invokeSuggestSupplierRisk({
        supplierName: form.name || undefined,
        location: form.location || undefined,
        newsSnippet: undefined,
        suggestMitigationsOnly: false,
      })
      if (suggested.risk_scores) setForm((f) => ({ ...f, risk_scores: suggested.risk_scores as Record<string, unknown> }))
      if (typeof suggested.overall_score === 'number') setForm((f) => ({ ...f, overall_score: suggested.overall_score! }))
      if (suggested.outlook != null) setForm((f) => ({ ...f, outlook: suggested.outlook! }))
      if (suggested.actions != null) setForm((f) => ({ ...f, actions: suggested.actions! }))
      if (Array.isArray(suggested.mitigation_tasks) && suggested.mitigation_tasks.length > 0) {
        setSuggestedTasks(suggested.mitigation_tasks)
      }
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'AI suggestion failed. Configure AI in AI Settings.')
    } finally {
      setAiLoading(false)
    }
  }

  const handleSuggestMitigations = async () => {
    if (!selectedSupplier) return
    setAiLoading(true)
    setAiError(null)
    try {
      const { suggested } = await invokeSuggestSupplierRisk({
        supplierName: selectedSupplier.name,
        location: selectedSupplier.location ?? undefined,
        suggestMitigationsOnly: true,
        supplierId: selectedSupplier.id,
      })
      if (Array.isArray(suggested.mitigation_tasks)) setSuggestedTasks(suggested.mitigation_tasks)
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'AI suggestion failed. Configure AI in AI Settings.')
    } finally {
      setAiLoading(false)
    }
  }

  const handleSaveSupplier = async () => {
    const name = (form.name ?? '').trim()
    if (!name) return
    const payload = {
      ...form,
      name,
      location: form.location?.trim() || null,
      risk_rating: form.risk_rating?.trim() || null,
      industry: form.industry?.trim() || null,
      revenue: form.revenue?.trim() || null,
      growth: form.growth?.trim() || null,
      employees: form.employees?.trim() || null,
      years_in_business: form.years_in_business ?? null,
      risk_scores: form.risk_scores ?? {},
      overall_score: form.overall_score ?? null,
      outlook: form.outlook?.trim() || null,
      actions: form.actions?.trim() || null,
    }
    try {
      let supplierId: string
      if (modalMode === 'add') {
        const newSupplier = await insertSupplier({ ...payload, company_id: null })
        supplierId = newSupplier.id
        // Update visibility for new supplier if admin (empty array = global supplier)
        if (isAdmin) {
          try {
            await updateVisibility({ supplierId, companyIds: selectedCompanyIds })
          } catch (visibilityErr) {
            setAiError(visibilityErr instanceof Error ? `Failed to update visibility: ${visibilityErr.message}` : 'Failed to update supplier visibility')
            return
          }
        }
      } else if (selectedSupplier) {
        await updateSupplier({ id: selectedSupplier.id, ...payload })
        supplierId = selectedSupplier.id
        // Update visibility if admin (empty array = global supplier)
        if (isAdmin) {
          try {
            await updateVisibility({ supplierId, companyIds: selectedCompanyIds })
          } catch (visibilityErr) {
            setAiError(visibilityErr instanceof Error ? `Failed to update visibility: ${visibilityErr.message}` : 'Failed to update supplier visibility')
            return
          }
        }
      } else {
        return
      }
      closeModal()
      refetch()
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to save supplier')
    }
  }

  const handleAddTask = async () => {
    if (!selectedSupplier || !newTask.task.trim()) return
    try {
      await insertTask({
        supplier_id: selectedSupplier.id,
        company_id: selectedSupplier.company_id,
        task: newTask.task.trim(),
        status: newTask.status,
        priority: newTask.priority,
      })
      setNewTask({ task: '', status: 'scheduled', priority: 'medium' })
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to add task')
    }
  }

  const handleAddSuggestedTask = async (t: { task: string; status: string; priority: string }) => {
    if (!selectedSupplier) return
    const status = (STATUS_OPTIONS.includes(t.status as SupplierMitigationStatus) ? t.status : 'scheduled') as SupplierMitigationStatus
    const priority = (PRIORITY_OPTIONS.includes(t.priority as SupplierMitigationPriority) ? t.priority : 'medium') as SupplierMitigationPriority
    try {
      await insertTask({
        supplier_id: selectedSupplier.id,
        company_id: selectedSupplier.company_id,
        task: t.task,
        status,
        priority,
      })
      setSuggestedTasks((prev) => prev.filter((x) => x !== t))
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to add task')
    }
  }

  const riskScoresStr = useMemo(
    () => riskScoresToJson(form.risk_scores ?? {}),
    [form.risk_scores]
  )

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setImportMessage(null)
    setParseResult(null)
    setSelectedSheetIndices([])
    if (!file) {
      setImportFile(null)
      return
    }
    setImportFile(file)
    try {
      const result = await parseExcelFile(file)
      setParseResult(result)
      const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ')
      const candidateSheets = result.sheets
        .map((_, i) => i)
        .filter((i) => !isTasksOrNewsSheetName(norm(result.sheets[i].name)))
      const prefer = [
        'direct with commentary',
        'indirect with commentary',
        'kenvue master',
        'intelligence view',
        'table 4',
        'supplier risk',
        'risk assessment',
        'dashboard',
      ]
      const excludeFromDefault = [
        'table 1',
        'table 2',
        'table 3',
        'instruction',
        'weights',
        'composition',
        'internal data',
        'external data',
        'risk assistant',
        'executive summary',
        'mitigation',
        'special reports',
        'user groups',
        'on-hold',
        'on hold',
      ]
      const preferredSheets = candidateSheets.filter((i) => prefer.some((p) => norm(result.sheets[i].name).includes(p)))
      const defaultSheets = candidateSheets.filter((i) => !excludeFromDefault.some((e) => norm(result.sheets[i].name).includes(e)))
      const defaultIndices = preferredSheets.length > 0 ? preferredSheets : (defaultSheets.length > 0 ? defaultSheets : candidateSheets)
      setSelectedSheetIndices(defaultIndices.length > 0 ? defaultIndices : [])
    } catch (err) {
      setParseResult({
        sheets: [],
        supplierRows: [],
        taskRows: [],
        newsRows: [],
        userGroupRows: [],
        errors: [err instanceof Error ? err.message : 'Failed to parse Excel file.'],
      })
    }
  }

  const handleImport = async () => {
    if (!importPreview?.length || importErrors.length > 0) return
    setImporting(true)
    setImportMessage(null)
    try {
      const payloads = supplierRowsToInsertPayload(importPreview)
      const { inserted, updated, errors } = await upsertSuppliersInChunks(supabase, payloads, {
        assignCompanyId: companyId || null,
      })
      let groupsUpserted = 0
      if (companyId && (parseResult?.userGroupRows?.length ?? 0) > 0) {
        const g = await upsertUserGroupRows(supabase, companyId, parseResult!.userGroupRows)
        groupsUpserted = g.upserted
        errors.push(...g.errors)
      }
      let tasksInserted = 0
      let newsInserted = 0
      const hasTasksOrNews = (parseResult?.taskRows?.length ?? 0) > 0 || (parseResult?.newsRows?.length ?? 0) > 0
      if (hasTasksOrNews) {
        const names = [...new Set(importPreview.map((r) => r.name.trim()))]
        const newsEntityNames =
          (parseResult?.newsRows ?? []).map((r) => r.entity_name.trim()).filter(Boolean)
        const allNames = [...new Set([...names, ...newsEntityNames])]
        if (allNames.length > 0) {
          let suppliersQ = supabase
            .from('suppliers')
            .select('id, name, location, kenvue_region, supplier_unique_id')
            .in('name', allNames)
          if (companyId) {
            suppliersQ = suppliersQ.or(`company_id.is.null,company_id.eq.${companyId}`)
          } else {
            suppliersQ = suppliersQ.is('company_id', null)
          }
          const { data: suppliersData } = await suppliersQ
          // Also load all suppliers for fuzzy name match when exact .in() misses short aliases
          let allSuppliersQ = supabase
            .from('suppliers')
            .select('id, name, location, kenvue_region, supplier_unique_id')
          if (companyId) {
            allSuppliersQ = allSuppliersQ.or(`company_id.is.null,company_id.eq.${companyId}`)
          } else {
            allSuppliersQ = allSuppliersQ.is('company_id', null)
          }
          const { data: allSuppliersData } = await allSuppliersQ
          const supplierRows = (allSuppliersData?.length ? allSuppliersData : suppliersData) ?? []
          const nameToId = buildSupplierNameIndex(
            supplierRows.map((r) => ({ id: (r as { id: string }).id, name: (r as { name: string }).name }))
          )
          const uniqueIdToId = new Map<string, string>()
          for (const row of supplierRows) {
            const r = row as { id: string; supplier_unique_id?: string | null }
            const uid = (r.supplier_unique_id ?? '').trim()
            if (uid && !uniqueIdToId.has(uid)) uniqueIdToId.set(uid, r.id)
          }
          const supplierMeta = new Map<
            string,
            { location?: string | null; kenvue_region?: string | null; name?: string }
          >()
          for (const row of supplierRows) {
            const r = row as { id: string; name: string; location?: string | null; kenvue_region?: string | null }
            supplierMeta.set(r.id, {
              name: r.name,
              location: r.location,
              kenvue_region: r.kenvue_region,
            })
          }
        if (importTaskRows.length > 0) {
          const { payloads: taskPayloads, unresolved } = taskRowsToInsertPayload(
            importTaskRows,
            nameToId,
            companyId ?? null,
            uniqueIdToId
          )
          if (unresolved.length > 0) {
            errors.push(`Tasks: unknown supplier name(s): ${unresolved.slice(0, 5).join(', ')}${unresolved.length > 5 ? '…' : ''}`)
          }
          const taskResult = await insertMitigationTasksInChunks(supabase, taskPayloads)
          tasksInserted = taskResult.inserted
          errors.push(...taskResult.errors)
        }
        if ((parseResult?.newsRows?.length ?? 0) > 0) {
          const { payloads: newsPayloads, unresolved: newsUnresolved } = newsRowsToInsertPayload(
            parseResult!.newsRows,
            nameToId,
            companyId ?? null,
            supplierMeta
          )
          if (newsUnresolved.length > 0) {
            errors.push(`News: unknown entity name(s): ${newsUnresolved.slice(0, 5).join(', ')}${newsUnresolved.length > 5 ? '…' : ''}`)
          }
          const newsPayloadsToInsert = await filterExistingNewsPayloads(supabase, newsPayloads)
          const newsResult = await insertNewsRowsInChunks(supabase, newsPayloadsToInsert)
          newsInserted = newsResult.inserted
          errors.push(...newsResult.errors)
        }
        }
      }
      await refetch()
      invalidateSupplierRosterCache()
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      queryClient.invalidateQueries({ queryKey: ['supplier_mitigation_tasks'] })
      queryClient.invalidateQueries({ queryKey: ['supplier_news'] })
      const supplierMsg =
        updated > 0
          ? `Imported ${inserted} supplier(s), updated ${updated} existing.`
          : `Imported ${inserted} supplier(s) successfully.`
      const extra: string[] = []
      if (tasksInserted > 0) extra.push(`${tasksInserted} task(s)`)
      if (newsInserted > 0) extra.push(`${newsInserted} news item(s)`)
      if (groupsUpserted > 0) extra.push(`${groupsUpserted} user group(s)`)
      const extraStr = extra.length > 0 ? ` ${extra.join(', ')} imported.` : ''
      const noTasksOrNewsInFile =
        (parseResult?.taskRows?.length ?? 0) === 0 && (parseResult?.newsRows?.length ?? 0) === 0
      const suppliersOnlyHint =
        noTasksOrNewsInFile && (inserted > 0 || updated > 0)
          ? ' No Mitigation or News sheet found; only suppliers were imported. Add sheets named Mitigation/Tasks and News Repository/News to import tasks and news.'
          : ''
      const importSummary =
        errors.length > 0
          ? `${supplierMsg}${extraStr} Errors: ${errors.join('; ')}${suppliersOnlyHint}`
          : (extraStr ? `${supplierMsg} ${extraStr}` : supplierMsg) + suppliersOnlyHint
      setImportMessage(`${importSummary} Embedding for Risk analyst…`)

      try {
        const embedSummary = await runBackfillEmbeddingsUntilDone({
          onProgress: ({ round, embedded, failed }) => {
            setImportMessage(
              `${importSummary} Embedding… batch ${round} (embedded ${embedded}, failed ${failed})`
            )
          },
        })
        setImportMessage(
          `${importSummary} Embeddings: ${embedSummary.embedded} embedded, ${embedSummary.failed} failed.`
        )
      } catch (embedErr) {
        const embedMsg =
          embedErr instanceof Error ? embedErr.message : 'Embedding backfill failed.'
        setImportMessage(`${importSummary} Import saved; embeddings skipped: ${embedMsg}`)
      }

      setImportFile(null)
      setParseResult(null)
      setSelectedSheetIndices([])
    } catch (err) {
      setImportMessage(err instanceof Error ? err.message : 'Import failed.')
    } finally {
      setImporting(false)
    }
  }

  const handleDeleteEmptySuppliers = async () => {
    if (emptySuppliers.length === 0) return
    const ok = window.confirm(
      `Delete ${emptySuppliers.length} junk or blank supplier row(s)? Unscored Indirect/Direct master rows are not included. This cannot be undone.`,
    )
    if (!ok) return
    setCleaningUp(true)
    setCleanupMessage(null)
    try {
      let deleted = 0
      for (const s of emptySuppliers) {
        try {
          await deleteSupplier(s.id)
          deleted++
        } catch {
          // continue
        }
      }
      await refetch()
      setCleanupMessage(`Deleted ${deleted} junk/blank supplier row(s).`)
    } catch (err) {
      setCleanupMessage(err instanceof Error ? err.message : 'Clean up failed.')
    } finally {
      setCleaningUp(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-bold">
              <i className={`fa-solid ${SECTION_META[section].icon} mr-2`} aria-hidden />
              {SECTION_META[section].title}
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">{SECTION_META[section].intro}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {section === 'suppliers' && onOpenCompanySettings && (
              <button
                type="button"
                onClick={onOpenCompanySettings}
                className="text-sm text-blue-600 hover:underline font-medium"
              >
                Company settings
              </button>
            )}
            <button
              onClick={() => refetch()}
              className="text-sm text-blue-600 hover:underline font-medium"
            >
              <i className="fa-solid fa-arrow-rotate-right mr-1" aria-hidden />
              Refresh
            </button>
            {section === 'suppliers' && (
              <button
                onClick={openAdd}
                className="px-3 py-1.5 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-800"
              >
                Add supplier
              </button>
            )}
          </div>
        </div>
        {onNavigateSection && (
          <div className="mt-4 flex flex-wrap gap-2">
            {(Object.keys(SECTION_META) as EarlySignalsAdminSection[]).map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => onNavigateSection(id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  section === id
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                }`}
              >
                {SECTION_META[id].title}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="p-4 sm:p-6">
        {section === 'spend' && <SpendCubeImportPanel />}
        {section === 'import' && (
        <>
        <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">
            <i className="fa-solid fa-file-excel mr-1.5 text-green-600" aria-hidden />
            Import from Excel
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Upload a Direct/Indirect master workbook (.xlsx / .xls), or use <strong>Download template</strong> for
            the expected layout. Older Master / Intelligence View sheets still work.
          </p>
          <ul className="text-xs text-gray-600 mb-3 list-disc list-inside space-y-1">
            <li>
              <strong>Direct with Commentary</strong> — grain: supplier × brand. Entity Type, Supplier Name/ID,
              Location, Region, Brand, Category, Sub-Category, DM/EM, Single Source, Spend, 14 risk scores +
              commentaries, Final Risk (e.g. <code>2.5/10</code>), Highest Risk Areas, Next Best Action.
            </li>
            <li>
              <strong>Indirect with Commentary</strong> — grain: supplier × category × sub-category. Adds{' '}
              <strong>Supplier Parent Name</strong>. Blank risk scores on many rows are expected and kept.
            </li>
            <li>
              <strong>User groups risk monitoring</strong> — Function, Parent Group, Subgroup → portfolio monitoring
              groups.
            </li>
            <li>
              <strong>Mitigation Tracker</strong>: Date, Alert Description, Supplier Name, Location, Kenvue Region,
              Brand Name, Supplier Unique ID, Status, Comments (and related plant / category columns when filled).
            </li>
            <li>
              <strong>Optional:</strong> News of Suppliers / News sheet. Legacy Master / Intelligence View / Table 4
              sheets remain supported.
            </li>
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={downloadSuppliersTemplate}
              className="text-sm text-blue-600 hover:underline font-medium"
            >
              <i className="fa-solid fa-download mr-1" aria-hidden />
              Download template
            </button>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFileChange}
              className="text-sm file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-black file:text-white file:text-sm file:font-medium"
            />
            {importFile && (
              <span className="text-sm text-gray-600">{importFile.name}</span>
            )}
            {parseResult && supplierSheetIndices.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">Sheets:</span>
                <button
                  type="button"
                  onClick={() => setSelectedSheetIndices([...supplierSheetIndices])}
                  className="text-blue-600 hover:underline"
                >
                  Select all
                </button>
                <span className="text-gray-400">|</span>
                <button
                  type="button"
                  onClick={() => setSelectedSheetIndices([])}
                  className="text-blue-600 hover:underline"
                >
                  Deselect all
                </button>
                <div className="flex flex-wrap gap-3 ml-1">
                  {supplierSheetIndices.map((i) => {
                    const sheet = parseResult.sheets[i]
                    if (!sheet) return null
                    const checked = selectedSheetIndices.includes(i)
                    return (
                      <label key={sheet.name} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (checked) {
                              setSelectedSheetIndices((prev) => prev.filter((j) => j !== i))
                            } else {
                              setSelectedSheetIndices((prev) => [...prev, i].sort((a, b) => a - b))
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span>{sheet.name}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            )}
            {parseResult?.newsRows && parseResult.newsRows.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {parseResult.newsRows.length} news item(s) will be imported from News Repository sheet (Entity Name = supplier).
              </p>
            )}
            {importPreview && importPreview.length > 0 && (
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || importErrors.length > 0}
                className="px-3 py-1.5 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? 'Importing…' : `Import ${importPreview.length} supplier(s)`}
              </button>
            )}
          </div>
          {importErrors.length > 0 && (
            <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
              {importErrors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
          {hasNoInternalData && (
            <p className="mt-2 text-sm text-amber-700">
              No location/category data found. Select <strong>Direct with Commentary</strong> and/or{' '}
              <strong>Indirect with Commentary</strong> (or legacy Master / Intelligence View).
            </p>
          )}
          {importMessage && (
            <p className={`mt-2 text-sm ${importMessage.startsWith('Imported') && !importMessage.includes('Errors') ? 'text-green-700' : 'text-red-700'}`}>
              {importMessage}
            </p>
          )}
          {importPreview && importPreview.length > 0 && (
            <div className="mt-3 overflow-x-auto max-h-48 overflow-y-auto">
              {unscoredPreviewCount > 0 && (
                <p className="text-xs text-amber-800 mb-2">
                  {unscoredPreviewCount} row(s) have no risk scores (common on Indirect master — they will still
                  import).
                </p>
              )}
              <table className="min-w-full text-xs border border-gray-200 rounded">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Procurement</th>
                    <th className="p-2 text-left">Parent</th>
                    <th className="p-2 text-left">Location</th>
                    <th className="p-2 text-left">Spend $K</th>
                    <th className="p-2 text-left">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.slice(0, 15).map((r, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td className="p-2">{r.name}</td>
                      <td className="p-2">
                        {r.procurement_type === 'indirect'
                          ? 'Indirect'
                          : r.procurement_type === 'direct'
                            ? 'Direct'
                            : '—'}
                      </td>
                      <td className="p-2">{r.parent_name ?? '—'}</td>
                      <td className="p-2">{r.location ?? '—'}</td>
                      <td className="p-2">
                        {r.spend_k != null && Number.isFinite(Number(r.spend_k))
                          ? Number(r.spend_k).toLocaleString(undefined, { maximumFractionDigits: 1 })
                          : '—'}
                      </td>
                      <td className="p-2">{r.overall_score ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importPreview.length > 15 && (
                <p className="text-xs text-gray-500 mt-1">Showing first 15 of {importPreview.length} rows.</p>
              )}
            </div>
          )}
        </div>

        <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Clean up</h3>
          {unscoredMasterSuppliers.length > 0 && (
            <p className="text-sm text-gray-700 mb-3">
              {unscoredMasterSuppliers.length} supplier(s) have no risk scores (often unscored Indirect master
              rows). They remain in the portfolio — not deleted by cleanup.
            </p>
          )}
          {emptySuppliers.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-gray-700">
                {emptySuppliers.length} junk or blank supplier row(s) can be removed.
              </span>
              <button
                type="button"
                onClick={handleDeleteEmptySuppliers}
                disabled={cleaningUp}
                className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cleaningUp ? 'Deleting…' : 'Delete junk rows'}
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              {unscoredMasterSuppliers.length > 0
                ? 'No junk or blank rows to delete.'
                : 'All suppliers have risk data or valid master fields. Nothing to clean up.'}
            </p>
          )}
          {cleanupMessage && (
            <p className={`mt-2 text-sm ${cleanupMessage.startsWith('Deleted') ? 'text-green-700' : 'text-red-700'}`}>
              {cleanupMessage}
            </p>
          )}
        </div>
        </>
        )}

        {section === 'reports' && (
        <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">
            <i className="fa-solid fa-file-pdf mr-1.5 text-red-600" aria-hidden />
            Special reports
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Upload PDF, Word (.doc/.docx), or PowerPoint (.ppt/.pptx) per supplier. Users see and download these in the Early Signals &quot;Special reports&quot; tab. Max 25MB.
          </p>
          {(specialReportsUploadError || specialReportsDeleteError) && !specialReportsErrorDismissed && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm mb-3 flex items-center justify-between" role="alert">
              <span>{(specialReportsUploadError ?? specialReportsDeleteError) instanceof Error ? (specialReportsUploadError ?? specialReportsDeleteError)?.message : String(specialReportsUploadError ?? specialReportsDeleteError)}</span>
              <button type="button" onClick={() => setSpecialReportsErrorDismissed(true)} className="text-red-600 hover:text-red-800" aria-label="Dismiss">×</button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <label htmlFor="admin-special-reports-supplier" className="sr-only">Supplier</label>
            <select
              id="admin-special-reports-supplier"
              value={specialReportsSupplierId}
              onChange={(e) => setSpecialReportsSupplierId(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 min-w-[200px]"
              aria-label="Choose supplier"
            >
              <option value="">Choose supplier</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <input
              ref={specialReportsFileInputRef}
              type="file"
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file || !specialReportsSupplierId) return
                e.target.value = ''
                setSpecialReportsErrorDismissed(false)
                try {
                  await uploadReport({ supplierId: specialReportsSupplierId, file })
                  setSpecialReportsSupplierId('')
                } catch (_) {}
              }}
              className="hidden"
              aria-label="Select file to upload"
            />
            <button
              type="button"
              disabled={!specialReportsSupplierId || specialReportsUploading}
              onClick={() => specialReportsFileInputRef.current?.click()}
              className="text-sm font-semibold rounded-lg border-2 border-gray-300 px-4 py-2 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {specialReportsUploading ? <><i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />Uploading...</> : <><i className="fa-solid fa-upload mr-2" aria-hidden />Upload</>}
            </button>
          </div>
          {specialReportsLoading ? (
            <p className="text-sm text-gray-500">Loading reports...</p>
          ) : specialReports.length === 0 ? (
            <p className="text-sm text-gray-500">No special reports yet. Choose a supplier and upload a file above.</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {(() => {
                const bySupplier = new Map<string, SupplierSpecialReport[]>()
                for (const r of specialReports) {
                  const list = bySupplier.get(r.supplier_id) ?? []
                  list.push(r)
                  bySupplier.set(r.supplier_id, list)
                }
                return Array.from(bySupplier.entries()).map(([supplierId, list]) => {
                  const supplier = suppliers.find((s) => s.id === supplierId)
                  const name = (supplier && supplierDisplayName(supplier)) || 'Unknown'
                  return (
                    <div key={supplierId} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="font-medium text-gray-900 mb-2">{name}</div>
                      <ul className="space-y-1 text-sm">
                        {list.map((r) => (
                          <li key={r.id} className="flex items-center justify-between gap-2">
                            <span className="truncate text-gray-700">{r.file_name}</span>
                            <span className="flex items-center gap-2 shrink-0">
                              <button
                                type="button"
                                onClick={async () => {
                                  const url = await getDownloadUrl(r.file_path)
                                  if (url) window.open(url, '_blank', 'noopener,noreferrer')
                                }}
                                className="text-blue-600 hover:underline"
                              >
                                Download
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm(`Delete "${r.file_name}"?`)) return
                                  try {
                                    await deleteReport(r.id)
                                  } catch (_) {}
                                }}
                                className="text-red-600 hover:underline"
                                aria-label={`Delete ${r.file_name}`}
                              >
                                Delete
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })
              })()}
            </div>
          )}
        </div>
        )}

        {section === 'suppliers' && (
        <>
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm mb-4">
            {toErrorMessage(error, 'Error loading suppliers')}
          </div>
        )}
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
            Loading...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 font-semibold">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Procurement</th>
                  <th className="p-3">Parent</th>
                  <th className="p-3">Brand</th>
                  <th className="p-3">Sub-category</th>
                  <th className="p-3">Location</th>
                  <th className="p-3">Spend $K</th>
                  <th className="p-3">Risk score (0–10)</th>
                  <th className="p-3">Risk band</th>
                  <th className="p-3">Outlook</th>
                  <th className="p-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="p-8 text-center text-gray-500">
                      No suppliers. Click &quot;Add supplier&quot; to create one.
                    </td>
                  </tr>
                ) : (
                  suppliers.map((s) => (
                    <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3 font-medium">{s.name}</td>
                      <td className="p-3 text-gray-600">
                        {s.procurement_type === 'indirect'
                          ? 'Indirect'
                          : s.procurement_type === 'direct'
                            ? 'Direct'
                            : '—'}
                      </td>
                      <td className="p-3 text-gray-600 max-w-[160px] truncate" title={s.parent_name ?? ''}>
                        {s.parent_name?.trim() || '—'}
                      </td>
                      <td className="p-3 text-gray-600">{s.brand?.trim() || '—'}</td>
                      <td className="p-3 text-gray-600 max-w-[180px] truncate" title={s.sub_category ?? ''}>
                        {s.sub_category?.trim() || '—'}
                      </td>
                      <td className="p-3 text-gray-600">{s.location ?? '—'}</td>
                      <td className="p-3 text-gray-600">
                        {s.spend_k != null && Number.isFinite(Number(s.spend_k))
                          ? Number(s.spend_k).toLocaleString(undefined, { maximumFractionDigits: 1 })
                          : '—'}
                      </td>
                      <td className="p-3">{s.overall_score != null ? s.overall_score : '—'}</td>
                      <td className="p-3">{riskCategory(s)}</td>
                      <td className="p-3 text-gray-600 max-w-[200px] truncate" title={s.outlook ?? ''}>
                        {s.outlook ?? '—'}
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => openEdit(s)}
                          className="text-blue-600 hover:underline mr-2"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm('Delete this supplier?')) {
                              await deleteSupplier(s.id)
                              refetch()
                            }
                          }}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      <Modal
        isOpen={modalMode !== null}
        onClose={closeModal}
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-start gap-4 mb-4">
          <h3 className="text-xl font-bold">
            {modalMode === 'add' ? 'Add supplier' : 'Edit supplier'}
          </h3>
          <button type="button" onClick={closeModal} className="text-gray-400 hover:text-black p-1">
            <i className="fa-solid fa-xmark text-xl" aria-hidden />
          </button>
        </div>

        {aiError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            {aiError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={form.name ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={form.location ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Risk score (0–10)</label>
              <input
                type="number"
                min={0}
                max={10}
                step={0.1}
                value={form.overall_score ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  setForm((f) => ({ ...f, overall_score: v === '' ? null : Number(v) }))
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Risk category (optional)</label>
              <input
                type="text"
                value={form.risk_rating ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, risk_rating: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. High, Medium, Low (or leave blank to derive from score)"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
            <input
              type="text"
              value={form.industry ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Outlook</label>
            <input
              type="text"
              value={form.outlook ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, outlook: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actions</label>
            <input
              type="text"
              value={form.actions ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, actions: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Risk scores (JSON)</label>
            <textarea
              value={riskScoresStr}
              onChange={(e) => setForm((f) => ({ ...f, risk_scores: parseRiskScoresJson(e.target.value) }))}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
              placeholder='{"strategic": 5, "compliance": 4, ...}'
            />
          </div>

          {isAdmin && (
            <div className="pt-2 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Which companies can see this supplier?
                {selectedCompanyIds.length === 0 && (
                  <span className="ml-2 text-xs font-normal text-amber-700">
                    (Currently global — visible to every company)
                  </span>
                )}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Leave all unchecked to keep the supplier global. Checking one or more companies restricts visibility to those companies only.
              </p>
              {visibilityLoading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {companies.length === 0 ? (
                    <p className="text-sm text-gray-500">No companies available</p>
                  ) : (
                    companies.map((company) => (
                      <label key={company.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedCompanyIds.includes(company.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCompanyIds((prev) => [...prev, company.id])
                            } else {
                              setSelectedCompanyIds((prev) => prev.filter((id) => id !== company.id))
                            }
                          }}
                          className="rounded border-gray-300 text-black focus:ring-black"
                        />
                        <span className="flex-1">{company.name}</span>
                        <span className="text-xs text-gray-500">({company.domain})</span>
                      </label>
                    ))
                  )}
                </div>
              )}
              {selectedCompanyIds.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  No companies selected = visible to every company (global).
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="button"
              onClick={handleGenerateWithAi}
              disabled={aiLoading}
              className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
            >
              {aiLoading ? (
                <><i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden /> Generating…</>
              ) : (
                <><i className="fa-solid fa-wand-magic-sparkles mr-2" aria-hidden /> Suggest risk assessment</>
              )}
            </button>
            <button
              type="button"
              onClick={handleSaveSupplier}
              disabled={visibilityUpdating}
              className="px-4 py-2 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              {visibilityUpdating ? 'Saving...' : 'Save supplier'}
            </button>
            <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-md text-sm">
              Cancel
            </button>
          </div>
        </div>

        {modalMode === 'edit' && selectedSupplier && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="text-lg font-bold mb-3">Mitigation tasks</h4>
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={handleSuggestMitigations}
                disabled={aiLoading}
                className="px-3 py-1.5 bg-gray-100 text-gray-800 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                {aiLoading ? <i className="fa-solid fa-spinner fa-spin mr-1" aria-hidden /> : <i className="fa-solid fa-wand-magic-sparkles mr-1" aria-hidden />}
                Suggest mitigations
              </button>
            </div>
            {suggestedTasks.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Suggested by AI – click Add to add each</p>
                <ul className="space-y-2">
                  {suggestedTasks.map((t, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-gray-800">{t.task}</span>
                      <button
                        type="button"
                        onClick={() => handleAddSuggestedTask(t)}
                        className="text-blue-600 hover:underline shrink-0"
                      >
                        Add
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2 mb-2 flex-wrap">
              <input
                type="text"
                value={newTask.task}
                onChange={(e) => setNewTask((t) => ({ ...t, task: e.target.value }))}
                placeholder="New task description"
                className="flex-1 min-w-[180px] border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <select
                value={newTask.status}
                onChange={(e) => setNewTask((t) => ({ ...t, status: e.target.value as SupplierMitigationStatus }))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={newTask.priority}
                onChange={(e) => setNewTask((t) => ({ ...t, priority: e.target.value as SupplierMitigationPriority }))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddTask}
                disabled={!newTask.task.trim()}
                className="px-3 py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
              >
                Add task
              </button>
            </div>
            <ul className="divide-y divide-gray-100">
              {tasks.map((t) => (
                <li key={t.id} className="py-2 text-sm">
                  {editingTaskId === t.id ? (
                    <TaskEditRow
                      task={t}
                      onSave={async (updates) => {
                        await updateTask({ id: t.id, ...updates })
                        setEditingTaskId(null)
                      }}
                      onCancel={() => setEditingTaskId(null)}
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-800">{t.task}</span>
                      <span className="text-gray-500 text-xs">{t.status} / {t.priority}</span>
                      <div>
                        <button
                          type="button"
                          onClick={() => setEditingTaskId(t.id)}
                          className="text-blue-600 hover:underline mr-2"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm('Delete this task?')) await deleteTask(t.id)
                          }}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {tasks.length === 0 && suggestedTasks.length === 0 && (
              <p className="text-gray-500 text-sm">No mitigation tasks yet. Add one above or use &quot;Suggest mitigations&quot;.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
